import { Card, Descriptions, Empty, Progress, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import type { DeviceDetailDto } from '@/types/device';

const { Text } = Typography;

interface Props {
  device: DeviceDetailDto;
}

/**
 * Convierte bytes a una representacion humana (KB / MB / GB).
 */
function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '—';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

export function DeviceInfoTab({ device }: Props) {
  const hw = device.hardwareInfo;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* Identificacion */}
      <Card type="inner" title="Identificacion" size="small">
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Numero de serie">
            {device.serialNumber ? <Text code>{device.serialNumber}</Text> : <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="IMEI">
            {device.imeiNumber ? <Text code>{device.imeiNumber}</Text> : <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="ID de Google">
            {device.googleDeviceId ? <Text code style={{ fontSize: 11 }}>{device.googleDeviceId}</Text> : <Text type="secondary">No enrolado</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="PIN esperado">
            <Text code>{device.expectedPin}</Text>
          </Descriptions.Item>
          {device.googleResourceName && (
            <Descriptions.Item label="Resource name" span={2}>
              <Text code style={{ fontSize: 11 }}>{device.googleResourceName}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Hardware */}
      <Card type="inner" title="Hardware" size="small">
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Marca">{device.brand || <Text type="secondary">—</Text>}</Descriptions.Item>
          <Descriptions.Item label="Modelo">{device.model || <Text type="secondary">—</Text>}</Descriptions.Item>
          <Descriptions.Item label="Android">
            {device.androidVersion ? <Tag color="green">{device.androidVersion}</Tag> : <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="API level">{device.androidApiLevel ?? <Text type="secondary">—</Text>}</Descriptions.Item>

          {hw && (
            <>
              <Descriptions.Item label="RAM total">{formatBytes(hw.totalRamBytes)}</Descriptions.Item>
              <Descriptions.Item label="Storage interno">{formatBytes(hw.totalInternalStorageBytes)}</Descriptions.Item>
              {hw.batteryLevelPercent != null && (
                <Descriptions.Item label="Bateria" span={2}>
                  <Progress
                    percent={hw.batteryLevelPercent}
                    size="small"
                    status={hw.batteryLevelPercent < 20 ? 'exception' : 'normal'}
                    style={{ maxWidth: 300 }}
                  />
                </Descriptions.Item>
              )}
              {hw.lastBootTime && (
                <Descriptions.Item label="Ultimo arranque" span={2}>
                  {dayjs(hw.lastBootTime).format('DD/MM/YYYY HH:mm')}
                  <Text type="secondary"> ({dayjs(hw.lastBootTime).fromNow()})</Text>
                </Descriptions.Item>
              )}
            </>
          )}
        </Descriptions>
      </Card>

      {/* Asignacion */}
      <Card type="inner" title="Asignacion" size="small">
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Asignado a">
            {device.assignedToName || <Text type="secondary" italic>Sin asignar</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Email">{device.assignedToEmail || <Text type="secondary">—</Text>}</Descriptions.Item>
          <Descriptions.Item label="Telefono">{device.assignedToPhone || <Text type="secondary">—</Text>}</Descriptions.Item>
          <Descriptions.Item label="Enterprise">{device.enterpriseName}</Descriptions.Item>
          <Descriptions.Item label="Perfil">
            {device.profileName ? <Tag color="blue">{device.profileName}</Tag> : <Text type="secondary" italic>Sin perfil</Text>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Software / red */}
      {hw && (hw.androidBuildNumber || hw.securityPatchLevel || hw.kernelVersion || hw.bootloader || hw.wifiMacAddress || hw.cellularNetworkOperator || hw.phoneNumber) && (
        <Card type="inner" title="Software & Red" size="small">
          <Descriptions column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label="Build number">{hw.androidBuildNumber || <Text type="secondary">—</Text>}</Descriptions.Item>
            <Descriptions.Item label="Security patch">{hw.securityPatchLevel || <Text type="secondary">—</Text>}</Descriptions.Item>
            <Descriptions.Item label="Bootloader">{hw.bootloader || <Text type="secondary">—</Text>}</Descriptions.Item>
            <Descriptions.Item label="Kernel">
              <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: hw.kernelVersion }}>{hw.kernelVersion || '—'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="WiFi MAC">{hw.wifiMacAddress ? <Text code>{hw.wifiMacAddress}</Text> : <Text type="secondary">—</Text>}</Descriptions.Item>
            <Descriptions.Item label="Operador">{hw.cellularNetworkOperator || <Text type="secondary">—</Text>}</Descriptions.Item>
            <Descriptions.Item label="Telefono SIM" span={2}>{hw.phoneNumber || <Text type="secondary">—</Text>}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Timestamps */}
      <Card type="inner" title="Fechas" size="small">
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Creado">{dayjs(device.createdAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
          <Descriptions.Item label="Enrolado">
            {device.enrolledAt ? dayjs(device.enrolledAt).format('DD/MM/YYYY HH:mm') : <Text type="secondary">No enrolado</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Ultimo sync" span={2}>
            {device.lastSyncAt
              ? <>
                  {dayjs(device.lastSyncAt).format('DD/MM/YYYY HH:mm:ss')}
                  <Text type="secondary"> ({dayjs(device.lastSyncAt).fromNow()})</Text>
                </>
              : <Text type="secondary">Nunca</Text>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {!hw && device.isEnrolledInGoogle && (
        <Empty
          description="Google aun no reporto info detallada del device. Esto puede tardar unos minutos despues del primer enrolamiento. Probar el comando 'Sincronizar'."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Space>
  );
}
